import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Container, Input, Table } from "reactstrap";
import './CommandeLine.css';
import { FaPlus, FaTrash } from "react-icons/fa";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { productActions } from "../../sagas/productSlice";
import { formatPrice, preventScroll, roundToNearestInteger } from "../../helpers/utility";

const DevisLine = ({ formik, disabled }) => {
    const { values, setFieldValue } = formik;
    const [items, setItems] = useState([]);
    const [articleSearchByIndex, setArticleSearchByIndex] = useState({});
    const [activeSearchIndex, setActiveSearchIndex] = useState(null);
    const articleInputRefs = useRef({});

    const unites = useSelector(status => status.products.unites);
    const { products, pageSize, loading } = useSelector((status) => status.products);

    const dispatch = useDispatch();

    useEffect(() => {
        setItems(values.lignes.map(l => {
            const product = products?.find(p => p.id === l.articleId);
            let prices = l.prices;
            if (!prices && product) {
                prices = [product.prixVenteTTC1, product.prixVenteTTC2, product.prixVenteTTC3].filter(Boolean);
            } else if (!prices) {
                prices = [l.prixUnitaireTTC].filter(Boolean);
            }
            return {
                ...l,
                unite: unites.find(u => u.code === l.unite)?.libelle,
                prices,
            };
        }));
    }, [dispatch, products, unites, values.lignes]);

    const handleInputChange = (index, event) => {
        const toNum = v => {
            const n = parseFloat(v);
            return Number.isFinite(n) ? n : 0;
        };
        const toCents = v => Math.round(v * 100);
        const fromCents = c => (c / 100).toFixed(2);

        const newItems = [...items];
        const updatedItem = { ...newItems[index], [event.target.name]: event.target.value };
        newItems[index] = updatedItem;

        const quantity = toNum(newItems[index].quantite);
        const unitTTC = toNum(newItems[index].prixUnitaireTTC);
        const tauxTVA = toNum(newItems[index].tauxTVA);
        const unitHT = tauxTVA > -1 ? (unitTTC / (1 + tauxTVA)) : unitTTC;
        const lineTTCc = toCents(quantity * unitTTC);
        const lineHTc = toCents(quantity * unitHT);

        newItems[index].prixUnitaireHT = fromCents(toCents(unitHT));
        newItems[index].montantHT = fromCents(lineHTc);
        newItems[index].montantTTC = fromCents(lineTTCc);

        let htCumulC = 0;
        let ttcCumulC = 0;
        let taxeTotalC = 0;

        newItems.forEach(item => {
            const q = toNum(item.quantite);
            const uTTC = toNum(item.prixUnitaireTTC);
            const tva = toNum(item.tauxTVA);
            const uHT = tva > -1 ? (uTTC / (1 + tva)) : uTTC;
            const ttcC = toCents(q * uTTC);
            const htC = toCents(q * uHT);
            const tvaC = ttcC - htC;

            htCumulC += htC;
            ttcCumulC += ttcC;
            taxeTotalC += tvaC;
        });

        setItems(newItems);
        setFieldValue('lignes', newItems);
        setFieldValue('montantTotalHT', fromCents(htCumulC));
        setFieldValue('montantTotalTTC', fromCents(ttcCumulC));
        setFieldValue('montantTotalTaxe', fromCents(taxeTotalC));
    };

    const addNewLine = () => {
        const newItem = {
            articleId: null,
            articleNom: '',
            prices: [],
            quantite: 1,
            prixUnitaireTTC: 0,
            prixUnitaireHT: 0,
            unite: '',
            tauxTVA: 0.2,
            montantHT: 0,
            montantTTC: 0,
        };
        const newItems = [...items, newItem];
        setItems(newItems);
        setFieldValue('lignes', newItems);
        setActiveSearchIndex(newItems.length - 1);
    };

    const handleDeleteItem = (index) => {
        const toNum = v => {
            const n = parseFloat(v);
            return Number.isFinite(n) ? n : 0;
        };
        const toCents = v => Math.round(v * 100);
        const fromCents = c => (c / 100).toFixed(2);

        const newItems = [...items];
        newItems.splice(index, 1);

        setItems(newItems);
        setFieldValue('lignes', newItems);

        let htCumulC = 0;
        let ttcCumulC = 0;
        let taxeTotalC = 0;

        newItems.forEach(item => {
            const q = toNum(item.quantite);
            const tva = toNum(item.tauxTVA);

            let unitTTC = toNum(item.prixUnitaireTTC);
            let unitHT;

            if (unitTTC > 0) {
                unitHT = unitTTC / (1 + tva);
            } else {
                unitHT = toNum(item.prixUnitaireHT);
                unitTTC = unitHT * (1 + tva);
            }

            const lineTTCc = toCents(q * unitTTC);
            const lineHTc = toCents(q * unitHT);
            const lineTVAc = lineTTCc - lineHTc;

            htCumulC += lineHTc;
            ttcCumulC += lineTTCc;
            taxeTotalC += lineTVAc;
        });

        setFieldValue('montantTotalHT', fromCents(htCumulC));
        setFieldValue('montantTotalTTC', fromCents(ttcCumulC));
        setFieldValue('montantTotalTaxe', fromCents(taxeTotalC));
    };

    const debouncedArticleSearch = useMemo(() =>
        debounce((query) => {
            if (!query || !query.trim()) return;
            dispatch(productActions.findAll({
                search: query,
                page: 0,
                size: pageSize
            }));
        }, 300)
        , [dispatch, pageSize]);

    useEffect(() => {
        return () => debouncedArticleSearch.cancel();
    }, [debouncedArticleSearch]);

    useEffect(() => {
        if (activeSearchIndex === null) return;
        const inputEl = articleInputRefs.current[activeSearchIndex];
        if (inputEl) {
            inputEl.focus();
            inputEl.select?.();
        }
    }, [activeSearchIndex, items.length]);

    const handleArticleSearchChange = (index, value) => {
        setArticleSearchByIndex(prev => ({ ...prev, [index]: value }));
        setActiveSearchIndex(index);
        debouncedArticleSearch(value);
    };

    const handleProductSelect = (product, lineIndex) => {
        if (!values.clientId) {
            Swal.fire({
                icon: 'warning',
                title: t('devis.noClientTitle'),
                text: t('devis.noClientMessage'),
                confirmButtonText: t('common.ok', 'OK')
            });

            return;
        }

        const productExists = items.some((item, index) => index !== lineIndex && item.articleId === product.id);

        if (productExists) {
            Swal.fire({
                icon: 'warning',
                title: t('purchase.productExistsTitle'),
                text: t('purchase.productExistsMessage'),
                confirmButtonText: t('common.ok')
            });
            return;
        }
        const prices = [product.prixVenteTTC1, product.prixVenteTTC2, product.prixVenteTTC3];

        const newItem = {
            articleId: product.id,
            articleNom: product.libelle,
            prices: prices,
            quantite: 1,
            prixUnitaireTTC: roundToNearestInteger(product.prixVenteTTC1),
            prixUnitaireHT: roundToNearestInteger(product.prixVenteTTC1 / (1 + (product.tauxTVAVente || 0.2))),
            unite: unites.find(u => u.code === product.unite)?.libelle || '',
            tauxTVA: product.tauxTVAVente || 0.2,
            montantHT: roundToNearestInteger(product.prixVenteTTC1 / (1 + (product.tauxTVAVente || 0.2))),
            montantTTC: roundToNearestInteger(product.prixVenteTTC1),
        };

        const newItems = [...items];
        newItems[lineIndex] = newItem;
        setItems(newItems);
        setFieldValue('lignes', newItems);

        let newHtCumul = 0;
        let newTtcCumul = 0;
        let montantTotalTaxe = 0;

        newItems.forEach(item => {
            const quantity = parseFloat(item.quantite) || 0;
            const unitPrice = parseFloat(item.prixUnitaireHT) || 0;
            const tauxTVA = parseFloat(item.tauxTVA) || 0;

            newHtCumul += quantity * unitPrice;
            newTtcCumul += (quantity * unitPrice) * (1 + tauxTVA);
            montantTotalTaxe += (quantity * unitPrice) * tauxTVA;
        });

        setFieldValue('montantTotalTaxe', roundToNearestInteger(montantTotalTaxe));
        setFieldValue('montantTotalHT', roundToNearestInteger(newHtCumul));
        setFieldValue('montantTotalTTC', roundToNearestInteger(newTtcCumul));
        setArticleSearchByIndex(prev => ({ ...prev, [lineIndex]: product.libelle }));
        setActiveSearchIndex(null);
    };

    return (
        <Container fluid>
            <Table bordered>
                <thead>
                    <tr>
                        <th style={{ width: "420px", minWidth: "420px" }}>{t('devis.article')}</th>
                        <th>{t('devis.quantite')}</th>
                        <th>{t('devis.unite')}</th>
                        <th style={{ width: "200px" }}>{t('devis.prixUnitaireTTC')}</th>
                        <th>{t('devis.prixVente')}</th>
                        <th>{t('devis.tauxTVA')}</th>
                        <th>{t('devis.montantTTC')}</th>
                        <th>{t('common.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr>
                            <td colSpan="8" className="text-center py-4">
                                <div className="text-muted">
                                    {t('purchase.noItems')}
                                </div>
                            </td>
                        </tr>
                    ) : (
                        items.map((item, index) => (
                            <tr key={index}>
                                <td>
                                    <div style={{ position: 'relative' }}>
                                        <Input
                                            type="text"
                                            name="articleNom"
                                            value={item.articleId ? item.articleNom : (articleSearchByIndex[index] ?? '')}
                                            onChange={(event) => handleArticleSearchChange(index, event.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }
                                            }}
                                            placeholder={item.articleId ? '' : t('product.searchPlaceholder')}
                                            disabled={disabled || Boolean(item.articleId)}
                                            innerRef={(el) => {
                                                if (el) articleInputRefs.current[index] = el;
                                            }}
                                        />
                                        {!item.articleId && activeSearchIndex === index && (articleSearchByIndex[index] ?? '').trim().length > 0 && (
                                            <div
                                                className="bg-white border rounded shadow-sm"
                                                style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    zIndex: 20,
                                                    maxHeight: '220px',
                                                    overflowY: 'auto'
                                                }}
                                            >
                                                {loading ? (
                                                    <div className="p-2 text-muted small">{t('common.loading', 'Loading...')}</div>
                                                ) : (!products || products.length === 0) ? (
                                                    <div className="p-2 text-muted small">{t('product.noProducts')}</div>
                                                ) : (
                                                    products.map((product) => (
                                                        <button
                                                            key={product.id}
                                                            type="button"
                                                            className="w-100 text-start border-0 bg-white px-2 py-2"
                                                            onClick={() => handleProductSelect(product, index)}
                                                        >
                                                            <div className="fw-semibold">{product.libelle}</div>
                                                            <div className="small text-muted">
                                                                {product.code} - {formatPrice(product.prixVenteTTC1)}
                                                            </div>
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <Input
                                        type="number"
                                        name="quantite"
                                        onFocus={(e) => {
                                            e.target.select();
                                            e.target.addEventListener('wheel', preventScroll, { passive: false });
                                        }}
                                        onBlur={(e) => {
                                            e.target.removeEventListener('wheel', preventScroll);
                                        }}
                                        className="no-spinner"
                                        value={item.quantite}
                                        onChange={(event) => handleInputChange(index, event)}
                                        disabled={disabled}
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        name="unite"
                                        value={item.unite}
                                        onChange={(event) => handleInputChange(index, event)}
                                        disabled
                                    />
                                </td>
                                <td style={{ width: '100px' }}>
                                    <Input
                                        type="select"
                                        name="prixUnitaireTTC"
                                        value={item.prixUnitaireTTC}
                                        onChange={(event) => handleInputChange(index, event)}
                                        disabled={disabled}
                                    >
                                        {item.prices && item.prices.map((price, priceIndex) => (
                                            <option key={priceIndex} value={price}>
                                                {formatPrice(price, false)}
                                            </option>
                                        ))}
                                    </Input>
                                </td>
                                <td>
                                    <Input
                                        type="number"
                                        name="prixUnitaireTTC"
                                        onFocus={(e) => {
                                            e.target.select();
                                            e.target.addEventListener('wheel', preventScroll, { passive: false });
                                        }}
                                        onBlur={(e) => {
                                            e.target.removeEventListener('wheel', preventScroll);
                                        }}
                                        className="no-spinner"
                                        value={item.prixUnitaireTTC}
                                        onChange={(event) => handleInputChange(index, event)}
                                        disabled={disabled}
                                    />
                                </td>
                                <td>
                                    <Input
                                        name="tauxTVA"
                                        value={`${(item.tauxTVA * 100).toFixed(0)} %`}
                                        disabled
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        name="montantTTC"
                                        value={formatPrice(item.montantTTC, false)}
                                        disabled
                                    />
                                </td>
                                <td>
                                    <Button color="danger" onClick={() => handleDeleteItem(index)} size="sm" disabled={disabled}>
                                        <FaTrash />
                                    </Button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
            <Button color="primary" onClick={addNewLine} disabled={disabled} className="mb-3">
                <FaPlus /> {t('facture.addLine')}
            </Button>
        </Container>
    );
};

export default DevisLine;
